import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  serviceCategories,
  allSubServices,
  testimonials,
  trustedBrands,
  aboutData,
  landscapeVideos,
  shortsVideos,
  podcastData,
  socialLinks,
  founderVideos,
  celebrityImages,
} from "../data/mock";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Search,
  ArrowRight,
  ArrowUpRight,
  Star,
  Play,
  X,
  Mic,
  Megaphone,
  Users,
  Video as VideoIcon,
  Newspaper,
  Globe,
  Zap,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Service icon mapping
const serviceIconMap = {
  "digital-marketing": Megaphone,
  "content-creation": VideoIcon,
  "publicity-pr": Newspaper,
  "web-design-it": Globe,
  "trending": Zap,
};

// Color gradient mapping for service cards
const serviceGradientMap = {
  "digital-marketing": "from-[#dc2626]/20 via-[#dc2626]/5 to-transparent",
  "content-creation": "from-[#f97316]/20 via-[#f97316]/5 to-transparent",
  "publicity-pr": "from-[#a855f7]/20 via-[#a855f7]/5 to-transparent",
  "web-design-it": "from-[#06b6d4]/20 via-[#06b6d4]/5 to-transparent",
  "trending": "from-[#10b981]/20 via-[#10b981]/5 to-transparent",
};

// Animated wave background component - reusable
const WaveBackground = ({ className = "", opacity = 0.04 }) => (
  <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
    {/* SVG Wave Animation */}
    <svg
      className="absolute bottom-0 left-0 w-full"
      viewBox="0 0 1440 320"
      preserveAspectRatio="none"
      style={{ height: "40%", opacity }}
    >
      <motion.path
        fill="url(#waveGradient)"
        animate={{
          d: [
            "M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
            "M0,128L48,154.7C96,181,192,235,288,234.7C384,235,480,181,576,181.3C672,181,768,235,864,250.7C960,267,1056,245,1152,229.3C1248,213,1344,203,1392,197.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
            "M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
          ],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <defs>
        <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#dc2626" />
          <stop offset="50%" stopColor="#ef4444" />
          <stop offset="100%" stopColor="#dc2626" />
        </linearGradient>
      </defs>
    </svg>
    {/* Second wave layer */}
    <svg
      className="absolute bottom-0 left-0 w-full"
      viewBox="0 0 1440 320"
      preserveAspectRatio="none"
      style={{ height: "35%", opacity: opacity * 0.6 }}
    >
      <motion.path
        fill="#dc2626"
        animate={{
          d: [
            "M0,256L48,240C96,224,192,192,288,181.3C384,171,480,181,576,197.3C672,213,768,235,864,229.3C960,224,1056,192,1152,181.3C1248,171,1344,181,1392,186.7L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
            "M0,224L48,213.3C96,203,192,181,288,186.7C384,192,480,224,576,229.3C672,235,768,213,864,197.3C960,181,1056,171,1152,181.3C1248,192,1344,224,1392,240L1440,256L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
            "M0,256L48,240C96,224,192,192,288,181.3C384,171,480,181,576,197.3C672,213,768,235,864,229.3C960,224,1056,192,1152,181.3C1248,171,1344,181,1392,186.7L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
          ],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
      />
    </svg>
  </div>
);

// Animated background for hero - wave animation + floating elements
const HeroBackground = () => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    // Generate particles
    const newParticles = [...Array(30)].map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 15 + 10,
      delay: Math.random() * 5,
    }));
    setParticles(newParticles);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Wave Animation Background */}
      <WaveBackground opacity={0.06} />

      {/* Gradient overlay at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-[40%] bg-gradient-to-t from-[#0a0a0a] to-transparent" />

      {/* Animated gradient orbs */}
      <motion.div
        className="absolute w-[600px] h-[600px] rounded-full opacity-[0.05]"
        style={{ 
          background: "radial-gradient(circle, #dc2626, transparent 70%)", 
          top: "5%", 
          right: "10%" 
        }}
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
          y: [0, 30, 0],
        }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute w-[500px] h-[500px] rounded-full opacity-[0.03]"
        style={{ 
          background: "radial-gradient(circle, #ef4444, transparent 70%)", 
          bottom: "20%", 
          left: "5%" 
        }}
        animate={{
          scale: [1.1, 1, 1.1],
          x: [0, -30, 0],
          y: [0, -20, 0],
        }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Floating particles */}
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-[#dc2626]"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
          }}
          animate={{
            y: [0, -100, 0],
            opacity: [0, 0.6, 0],
            scale: [0, 1, 0],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Connecting lines between particles */}
      <svg className="absolute inset-0 w-full h-full" style={{ opacity: 0.03 }}>
        <motion.line
          x1="10%" y1="30%" x2="40%" y2="60%"
          stroke="#dc2626" strokeWidth="1"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: [0, 1, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.line
          x1="60%" y1="20%" x2="90%" y2="50%"
          stroke="#dc2626" strokeWidth="1"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: [0, 1, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
        <motion.line
          x1="20%" y1="70%" x2="50%" y2="90%"
          stroke="#dc2626" strokeWidth="1"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: [0, 1, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
      </svg>

      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: "50px 50px",
        }}
      />
    </div>
  );
};

// Custom video player with thumbnail - removes YouTube branding
const CustomVideoPlayer = ({ youtubeId, title, isShort = false, className = "" }) => {
  const [playing, setPlaying] = useState(false);
  const thumbUrl = `https://img.youtube.com/vi/${youtubeId}/${isShort ? "0" : "maxresdefault"}.jpg`;

  // YouTube embed params to hide branding
  const embedParams = "rel=0&modestbranding=1&showinfo=0&iv_load_policy=3&controls=1&fs=1&cc_load_policy=0&disablekb=0&autoplay=1";

  if (playing) {
    return (
      <div className={`relative overflow-hidden rounded-xl bg-black ${className}`}>
        <iframe
          src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&${embedParams}`}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full absolute inset-0"
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-xl cursor-pointer group bg-black ${className}`}
      onClick={() => setPlaying(true)}
    >
      <img
        src={thumbUrl}
        alt={title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
      {/* Play button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="w-14 h-14 rounded-full bg-[#dc2626] flex items-center justify-center shadow-lg shadow-[#dc2626]/30"
        >
          <Play className="w-6 h-6 text-white fill-white ml-0.5" />
        </motion.div>
      </div>
      {/* Title at bottom */}
      {title && (
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
          <p className="text-white text-xs font-medium line-clamp-2">{title}</p>
        </div>
      )}
    </div>
  );
};

export default function HomePage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [activeVideoTab, setActiveVideoTab] = useState("feedback");
  const navigate = useNavigate();

  const searchResults = searchQuery.trim()
    ? allSubServices.filter(
        (s) =>
          s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.categoryTitle.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const duplicatedBrands = [...trustedBrands, ...trustedBrands];

  // Separate feedback videos from ad/promo videos
  const feedbackVideos = landscapeVideos.filter(v => v.type === "feedback");
  const promoVideos = landscapeVideos.filter(v => v.type === "ad");

  return (
    <div>
      {/* ===== HERO SECTION ===== */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden pt-20">
        <HeroBackground />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 w-full">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Badge
                variant="outline"
                className="border-[#dc2626]/30 bg-[#dc2626]/[0.08] text-[#ef4444] px-4 py-1.5 text-sm font-medium rounded-full mb-8"
              >
                Eastern India's Leading Digital Agency
              </Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1.08] tracking-tight mb-6"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              All Your Marketing
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#dc2626] to-[#ef4444]">
                Under One Roof
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-[#71717a] mb-10 max-w-xl mx-auto"
            >
              From digital marketing and content creation to PR and web design — we help brands scale smarter.
            </motion.p>

            {/* Search Bar */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
              className="relative max-w-xl mx-auto mb-10"
            >
              <div className={`relative flex items-center bg-white/[0.06] border rounded-2xl overflow-hidden transition-all duration-300 ${
                searchFocused ? "border-[#dc2626]/50 shadow-lg shadow-[#dc2626]/10" : "border-white/[0.08]"
              }`}>
                <Search className="w-5 h-5 text-[#52525b] ml-5 shrink-0" />
                <input
                  type="text" value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                  placeholder="Search services... e.g. Social Media, Google Ads, Reels"
                  className="flex-1 px-4 py-4 bg-transparent text-white text-sm placeholder:text-[#52525b] focus:outline-none"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery("")} className="mr-3 p-1 hover:bg-white/10 rounded-full transition-colors">
                    <X className="w-4 h-4 text-[#71717a]" />
                  </button>
                )}
              </div>
              <AnimatePresence>
                {searchQuery && searchFocused && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                    className="absolute top-full left-0 right-0 mt-2 bg-[#141414] border border-white/[0.08] rounded-xl overflow-hidden shadow-2xl z-50 max-h-80 overflow-y-auto"
                  >
                    {searchResults.length > 0 ? searchResults.map((result) => (
                      <Link key={result.id} to={`/services/${result.categoryId}#${result.id}`}
                        className="flex items-center gap-4 p-4 hover:bg-white/[0.04] transition-colors border-b border-white/[0.04] last:border-0"
                      >
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-white/[0.06]">
                          {(() => { const Icon = serviceIconMap[result.categoryId]; return Icon ? <Icon className="w-5 h-5 text-[#dc2626]" /> : null; })()}
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-white text-sm font-medium">{result.title}</p>
                          <p className="text-[#52525b] text-xs">{result.categoryTitle}</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-[#52525b]" />
                      </Link>
                    )) : (
                      <div className="p-6 text-center text-[#52525b] text-sm">No services found for "{searchQuery}"</div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Quick tags */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap justify-center gap-2 mb-12"
            >
              {["Social Media", "Google Ads", "Reels", "Personal Branding", "Website Design", "AI Tools"].map((tag) => (
                <button key={tag} onClick={() => setSearchQuery(tag)}
                  className="px-4 py-2 bg-white/[0.04] border border-white/[0.06] text-[#a1a1aa] text-xs rounded-full hover:border-[#dc2626]/30 hover:text-white hover:bg-[#dc2626]/[0.06] transition-all duration-300"
                >{tag}</button>
              ))}
            </motion.div>

            {/* Stats */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }}
              className="flex items-center justify-center gap-8 lg:gap-14"
            >
              {aboutData.stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl lg:text-3xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{stat.value}</div>
                  <p className="text-[#52525b] text-xs mt-1">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== SERVICE CATEGORIES - ICON BASED ===== */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        {/* Section wave background */}
        <WaveBackground opacity={0.02} />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-14">
            <Badge variant="outline" className="border-[#dc2626]/30 bg-[#dc2626]/[0.08] text-[#ef4444] px-4 py-1.5 text-sm rounded-full mb-6">Our Services</Badge>
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              What We <span className="text-[#dc2626]">Deliver</span>
            </h2>
            <p className="text-[#71717a] text-lg max-w-xl">Explore our complete suite of services designed to grow your brand at every stage.</p>
          </motion.div>

          {/* Bento grid with lucide icons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {serviceCategories.map((cat, i) => {
              const Icon = serviceIconMap[cat.id] || Zap;
              const gradient = serviceGradientMap[cat.id];
              return (
                <motion.div
                  key={cat.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className={`group relative rounded-2xl overflow-hidden border border-white/[0.06] hover:border-white/[0.15] transition-all duration-500 ${i === 0 ? "lg:row-span-2" : ""}`}
                >
                  <Link to={`/services/${cat.id}`} className="block h-full">
                    <div className={`relative p-8 ${i === 0 ? "min-h-[400px] lg:h-full" : "min-h-[220px]"} bg-gradient-to-br ${gradient} flex flex-col justify-end`}>
                      {/* Large background icon */}
                      <div className="absolute top-6 right-6 opacity-[0.06] group-hover:opacity-[0.1] transition-opacity duration-500">
                        <Icon className={`${i === 0 ? "w-32 h-32" : "w-24 h-24"}`} strokeWidth={1} />
                      </div>

                      {/* Colored accent line */}
                      <div className="w-10 h-1 rounded-full mb-4" style={{ backgroundColor: cat.color }} />

                      {/* Icon */}
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${cat.color}15` }}>
                        <Icon className="w-6 h-6" style={{ color: cat.color }} />
                      </div>

                      <h3 className="text-xl lg:text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                        {cat.title}
                      </h3>
                      <p className="text-[#a1a1aa] text-sm mb-5 max-w-sm">{cat.shortDesc}</p>

                      {/* Sub-service list */}
                      <div className="flex flex-wrap gap-2">
                        {cat.subServices.slice(0, i === 0 ? 6 : 3).map((sub) => (
                          <span key={sub.id} className="px-3 py-1 bg-white/[0.06] text-[#a1a1aa] text-[11px] rounded-full border border-white/[0.04]">
                            {sub.title.length > 28 ? sub.title.substring(0, 28) + "..." : sub.title}
                          </span>
                        ))}
                        {cat.subServices.length > (i === 0 ? 6 : 3) && (
                          <span className="px-3 py-1 text-[11px] rounded-full font-medium" style={{ backgroundColor: `${cat.color}20`, color: cat.color }}>
                            +{cat.subServices.length - (i === 0 ? 6 : 3)} more
                          </span>
                        )}
                      </div>

                      {/* Hover arrow */}
                      <div className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/[0.06] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                        <ArrowUpRight className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== PROMO VIDEO SECTION ===== */}
      {promoVideos.length > 0 && (
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
              className="text-center mb-8"
            >
              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Why Choose <span className="text-[#dc2626]">Cube?</span>
              </h2>
              <p className="text-[#71717a] text-sm">Watch how we help businesses grow through digital marketing.</p>
            </motion.div>
            <CustomVideoPlayer youtubeId={promoVideos[0].youtubeId} title={promoVideos[0].title} className="aspect-video border border-white/[0.06]" />
          </div>
        </section>
      )}

      {/* ===== CLIENT FEEDBACK VIDEOS ===== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <Badge variant="outline" className="border-[#dc2626]/30 bg-[#dc2626]/[0.08] text-[#ef4444] px-4 py-1.5 text-sm rounded-full mb-6">
              Real Results
            </Badge>
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Client <span className="text-[#dc2626]">Feedback</span>
            </h2>
            <p className="text-[#71717a] text-lg max-w-xl mx-auto">
              Don't take our word for it — hear directly from brands that grew with Cube.
            </p>
          </motion.div>

          {/* Tab Switcher */}
          <div className="flex items-center justify-center gap-2 mb-10">
            {[
              { id: "feedback", label: "Video Feedback", icon: VideoIcon },
              { id: "shorts", label: "Quick Reviews", icon: Play },
            ].map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => setActiveVideoTab(id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                  activeVideoTab === id ? "bg-[#dc2626] text-white" : "bg-white/[0.04] text-[#71717a] hover:text-white border border-white/[0.06]"
                }`}
              >
                <Icon className="w-4 h-4" />{label}
              </button>
            ))}
          </div>

          {/* Feedback Videos */}
          {activeVideoTab === "feedback" && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {feedbackVideos.map((video) => (
                  <CustomVideoPlayer key={video.id} youtubeId={video.youtubeId} title={video.title} className="aspect-video border border-white/[0.06]" />
                ))}
              </div>
            </motion.div>
          )}

          {/* Shorts */}
          {activeVideoTab === "shorts" && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {shortsVideos.slice(0, 10).map((video) => (
                  <div key={video.id}>
                    <CustomVideoPlayer youtubeId={video.youtubeId} title={video.title} isShort className="aspect-[9/16] border border-white/[0.06]" />
                    <p className="text-[#52525b] text-[10px] mt-1">{video.industry}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="relative text-center p-12 lg:p-20 rounded-3xl bg-white/[0.02] border border-white/[0.06] overflow-hidden"
          >
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#dc2626]/10 rounded-full blur-[120px]" />
            </div>
            <div className="relative">
              <Badge variant="outline" className="border-[#dc2626]/30 bg-[#dc2626]/[0.08] text-[#ef4444] px-4 py-1.5 text-sm rounded-full mb-8">Free Community</Badge>
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Grow with <span className="text-[#dc2626]">Sumanta</span>
              </h2>
              <p className="text-[#71717a] text-lg max-w-xl mx-auto mb-10">
                Join our WhatsApp community for free marketing tips, AI updates, business strategies, and exclusive sessions.
              </p>
              <Button onClick={() => navigate("/gws-whatsapp-community")}
                className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-6 md:px-10 py-4 md:py-6 rounded-xl text-sm md:text-base font-semibold transition-colors duration-300 flex items-center gap-2 mx-auto shadow-lg shadow-[#dc2626]/20"
              >
                <span className="hidden sm:inline">Join WhatsApp Community</span>
                <span className="sm:hidden">Join Community</span>
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== TEXT TESTIMONIALS ===== */}
      <section className="py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              What Our <span className="text-[#dc2626]">Clients Say</span>
            </h2>
          </motion.div>
        </div>
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
          <div className="flex gap-6 animate-marquee" style={{ width: "max-content" }}>
            {[...testimonials, ...testimonials].map((t, i) => (
              <div key={`${t.id}-${i}`} className="w-[350px] p-6 rounded-2xl bg-white/[0.03] border border-white/[0.08] hover:border-[#dc2626]/20 transition-all duration-500 shrink-0">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(t.rating)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-[#dc2626] text-[#dc2626]" />)}
                </div>
                <p className="text-[#a1a1aa] text-sm leading-relaxed mb-4 line-clamp-4">&ldquo;{t.review}&rdquo;</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#dc2626]/20 flex items-center justify-center text-[#dc2626] font-bold text-xs">{t.initial}</div>
                    <span className="text-white text-sm font-medium">{t.name}</span>
                  </div>
                  <span className="text-[#52525b] text-xs">{t.service}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PODCAST SECTION ===== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="relative p-10 lg:p-16 rounded-3xl bg-white/[0.02] border border-white/[0.06] overflow-hidden"
          >
            <div className="absolute -top-10 -right-10 w-60 h-60 bg-[#dc2626]/10 rounded-full blur-[100px] pointer-events-none" />
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#dc2626]/10 flex items-center justify-center">
                    <Mic className="w-6 h-6 text-[#dc2626]" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-xl" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{podcastData.name}</h3>
                    <p className="text-[#71717a] text-sm">Hosted by {podcastData.host}</p>
                  </div>
                </div>
                <p className="text-[#a1a1aa] text-base leading-relaxed mb-8">{podcastData.description}</p>
                <a href={podcastData.channelUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-[#dc2626]/30 text-[#dc2626] hover:bg-[#dc2626] hover:text-white px-6 py-3 rounded-xl text-sm transition-all duration-300 flex items-center gap-2">
                    Watch on YouTube<ArrowUpRight className="w-4 h-4" />
                  </Button>
                </a>
              </div>
              <CustomVideoPlayer youtubeId="Qf9LQObjEVc" title="Golpo Hok - Podcast by Sumanta Dey" className="aspect-video border border-white/[0.06]" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== CELEBRITIES SECTION ===== */}
      <section className="py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Celebrities <span className="text-[#dc2626]">Love Us</span>
            </h2>
            <p className="text-[#a1a1aa] text-base lg:text-lg max-w-2xl mx-auto">
              Our work speaks through the stars we've collaborated with
            </p>
          </motion.div>

          {/* Image Grid - Clean uniform layout */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
            {celebrityImages.map((image, index) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.03 }}
                className="relative group overflow-hidden rounded-xl"
              >
                <div className="relative aspect-square">
                  <img
                    src={image.src}
                    alt={`Celebrity moment ${image.id}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BRAND LOGOS ===== */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center mb-10">
          <h3 className="text-2xl lg:text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Trusted by <span className="text-[#dc2626]">India's Top Brands & Startups</span>
          </h3>
        </div>
        <div className="relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
          <div className="flex gap-12 items-center animate-marquee-brands" style={{ width: "max-content" }}>
            {duplicatedBrands.map((brand, i) => (
              <div key={`${brand.name}-${i}`} className={`shrink-0 flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity duration-300 ${brand.size === 'large' ? 'h-20 w-40' : 'h-14 w-28'}`}>
                <img src={brand.logo} alt={brand.name} className="max-h-full max-w-full object-contain brightness-0 invert" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
